import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Select, Space, DatePicker, Avatar, Badge, Row, Col, Input, Modal, Spin, message } from "antd";
import { CSVLink } from "react-csv";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useSelector } from "react-redux";
import Styled from "styled-components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CALL_API } from "../../../Common/api";
import { shipmentType } from "../../../Common/constant";
import { handleDownloadChart } from "../../../Reusablecomponents/Export/Exportpdf";
import { StyledTable } from "../../../Reusablecomponents/UITable/UITable";
import { ButtonFilled } from "../../../Reusablecomponents/UIButtons";
import ReusablePagination from "../../../Reusablecomponents/ReusablePagination";
import filtericon from "../../../assets/images/filter-svgrepo-com (1).svg";
import ExportIcon from "../../../assets/images/export-svgrepo-com (1).svg";
import { fetchNexusAssets, fetchNexusFences } from "../../TrackRightAI/nexusTrakingApi";
import { withNexusReportShell } from "../NexusReportShell";

dayjs.extend(utc);
dayjs.extend(timezone);

// Nexus Consignment Report: same layout as the AI Consignment Report (a tab of
// the Trip Report page), built from the Nexus consignments and trip events
// (POST nexus/consignmentreport).

const Styledashboard = Styled.div`
  .Custom { display: flex; align-items: center; justify-content: flex-end; margin-bottom: 20px; gap: 20px; }
  .dropstyle { margin-top: 0 !important; width: 251px !important; }
  .dropstyle1 { height: 36px; width: 251px !important; }
  .head { margin-bottom: 5px; color: #5f6d8c; height: 29px; }
  .header-container { display: flex; justify-content: space-between; align-items: center; width: 100%; }
  .hover-image { width: 22px; height: 22px; }
  .consignment-value { color: #1F243B; font-size: 24px; font-weight: 600; }
  .chart-box { position: relative; width: 100%; border: #E7E7ED 1px solid; border-radius: 8px; }
  .chart-title-row { display: flex; justify-content: space-between; align-items: center; padding: 20px; }
  .legend-container { display: flex; flex-wrap: wrap; gap: 10px 24px; justify-content: center; padding: 0 16px 16px; }
  .legend-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #5F6D8C; }
  .legend-color { width: 12px; height: 12px; border-radius: 50%; flex: none; }
  @media (max-width: 767px) {
    .chart-title-row { padding: 12px; }
  }
`;

const LOADING_COLORS = ["#73BEFF", "#FFFA75", "#AFCF66", "#D6FF75", "#9AFF75", "#75FFA3", "#75F4FF", "#05E7DC", "#52B4DA", "#FF75D3"];
const SMALL_THRESHOLD = 15;

const useIsMobile = (breakpoint = 768) => {
  const [mobile, setMobile] = useState(typeof window !== "undefined" && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return mobile;
};

const LoadingTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: "#fff", border: "0.5px solid #ddd", borderRadius: 10, padding: "10px 14px", fontSize: 12, minWidth: 160 }}>
      <p style={{ fontWeight: 500, marginBottom: 6, fontSize: 13 }}>{d.name}</p>
      <p style={{ color: "#888", display: "flex", justifyContent: "space-between", gap: 16 }}>
        Time <strong style={{ color: "#222" }}>{d.time}</strong>
      </p>
      <p style={{ color: "#888", display: "flex", justifyContent: "space-between", gap: 16 }}>
        Percentage <strong style={{ color: "#222" }}>{d.percent}%</strong>
      </p>
    </div>
  );
};

// Loading Cycle Time donut: labels beside the slices for a few consignors, hover
// tooltip plus a legend when there are many tiny slices or on a narrow screen
const LoadingCycleChart = ({ data, mobile }) => {
  const useTooltip = mobile || data.length > SMALL_THRESHOLD || data.some((item) => Number(item.percent) < 1);
  const renderLabel = ({ cx, cy, midAngle, outerRadius, payload }) => {
    if (!payload || payload.value === 0) return null;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25;
    const isLeft = midAngle > 90 && midAngle < 270;
    const bx = cx + radius * Math.cos(-midAngle * RADIAN);
    const by = cy + radius * Math.sin(-midAngle * RADIAN);
    const ex = bx + (isLeft ? -70 : 70);
    const clampedEx = isLeft ? Math.max(10, ex) : Math.min(cx * 2 - 10, ex);
    return (
      <g>
        <polyline
          points={`${cx + outerRadius * Math.cos(-midAngle * RADIAN)},${cy + outerRadius * Math.sin(-midAngle * RADIAN)} ${bx},${by} ${clampedEx},${by}`}
          stroke="#BEBEBE"
          fill="none"
          strokeWidth={1.5}
        />
        <circle cx={clampedEx} cy={by} r={7} fill={payload.color} />
        <text x={clampedEx + (isLeft ? -12 : 12)} y={by + 4} textAnchor={isLeft ? "end" : "start"} fill="#5F6D8C" fontSize={11} fontWeight={500}>
          {`${payload.name} ${payload.time} (${payload.percent}%)`}
        </text>
      </g>
    );
  };

  if (!data.length) {
    return <div style={{ textAlign: "center", color: "#5F6D8C", padding: "40px 0" }}>No loading cycle data for this period</div>;
  }

  return useTooltip ? (
    <>
      <div style={{ height: mobile ? 220 : 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={mobile ? 50 : 70} outerRadius={mobile ? 80 : 100} dataKey="value" label={false} labelLine={false} minAngle={1} paddingAngle={1}>
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<LoadingTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="legend-container">
        {data.map((item, i) => (
          <div key={i} className="legend-item">
            <span className="legend-color" style={{ backgroundColor: item.color }} />
            <span>
              {item.name} <strong>{item.percent}%</strong>
            </span>
          </div>
        ))}
      </div>
    </>
  ) : (
    <div style={{ height: 380, overflow: "visible" }}>
      <style>{`.recharts-wrapper svg, .recharts-surface { overflow: visible !important; }`}</style>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 80, right: 160, bottom: 80, left: 160 }}>
          <Pie data={data} cx="50%" cy="50%" innerRadius={70} outerRadius={100} dataKey="value" labelLine={false} label={renderLabel} minAngle={2} paddingAngle={1} isAnimationActive={false}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const Duration = [
  { label: "Current Month", value: "current_month" },
  { label: "Current Year", value: "current_year" },
  { label: "Last Month", value: "last_month" },
  { label: "Today", value: "today" },
];

const rangeFor = (duration) => {
  if (duration === "today") return [dayjs(), dayjs()];
  if (duration === "last_month")
    return [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")];
  if (duration === "current_year") return [dayjs().startOf("year"), dayjs().endOf("year")];
  return [dayjs().startOf("month"), dayjs().endOf("month")];
};

const eventDate = (date) => {
  if (!date) return "-";
  const dt = dayjs.utc(date).tz("Africa/Nairobi");
  return `${dt.format("DD/MM/YYYY")} ${dt.format("hh:mm A")}`;
};
const hhmm = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return "-";
  const total = Math.round(Number(minutes));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};
const tripTypeLabel = (v) => (v === 1 ? "Local" : v ? "Safari" : "-");

const { RangePicker } = DatePicker;
const { Option } = Select;

const EVENT_COLUMNS = [
  ["Started", "Started"],
  ["Arrive at pick-up point", "Arrived_at_pickup_point"],
  ["Delivery Journey Started", "Delivery_journey_started"],
  ["Reached Delivery Point", "Reached_delivery_point"],
  ["Return Journey Started", "Return_journey_started"],
  ["Returned to Base", "Returned_to_base"],
  ["Finished", "Finished"],
];

const NexusConsignmentReport = () => {
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);
  const transporterList = useSelector((state) => state?.authTracking?.transporterList);
  const reduxPermissions = useSelector((state) => state?.authTracking?.myPermissionList || {});
  const exportAllowed = useMemo(
    () => Array.isArray(reduxPermissions?.AITRPT) && reduxPermissions.AITRPT.includes("EX"),
    [reduxPermissions],
  );

  const [form] = Form.useForm();
  const [duration, setDuration] = useState("current_month");
  const [selectedDate, setSelectedDate] = useState(rangeFor("current_month"));
  const [filterValues, setFilterValues] = useState({});
  const [filterCount, setFilterCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({ data: [], total: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableSort, setTableSort] = useState({ field: null, order: null });
  const [CSVList, setCSVList] = useState([]);
  const [CSVLoader, setCSVLoader] = useState(false);
  const csvLinkEl = useRef();

  const isMobile = useIsMobile();

  const [vehicles, setVehicles] = useState([]);
  const [consignors, setConsignors] = useState([]);
  const [consignees, setConsignees] = useState([]);
  useEffect(() => {
    fetchNexusAssets()
      .then((r) => setVehicles(r.response || []))
      .catch(() => setVehicles([]));
    if (userInfo?.MCID === 59 || !userInfo?.NexusCompId) return;
    fetchNexusFences("Consignor", userInfo.NexusCompId).then(setConsignors).catch(() => setConsignors([]));
    fetchNexusFences("Consignee", userInfo.NexusCompId).then(setConsignees).catch(() => setConsignees([]));
  }, [userInfo?.MCID, userInfo?.NexusCompId]);

  useEffect(() => {
    setSelectedDate(rangeFor(duration));
  }, [duration]);

  const requestBody = useCallback(
    (page, size) => ({
      dateFrom: dayjs(selectedDate[0]).format("YYYY-MM-DD"),
      dateTo: dayjs(selectedDate[1]).format("YYYY-MM-DD"),
      page,
      pageSize: size,
      vehicle: filterValues?.vehicleno || "",
      consigner: filterValues?.consignor || "",
      transporter: filterValues?.transporter || "",
      consignee: filterValues?.fence || "",
      consignCode: filterValues?.consigncode || "",
      tripCode: filterValues?.trip || "",
      tripType: filterValues?.triptype || "",
      shipmentType: filterValues?.shipment || "",
    }),
    [selectedDate, filterValues],
  );

  const fetchReport = useCallback(async () => {
    if (!selectedDate?.[0] || !selectedDate?.[1]) return;
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "nexus/consignmentreport", requestBody(currentPage, pageSize));
      setReport({
        data: data?.data || [],
        total: data?.total || 0,
        statusChart: data?.ConsignmentStatusChart || [],
        loadingTime: data?.ConsignmentLoadingTime || [],
      });
    } catch (error) {
      console.error("[NexusConsignmentReport] fetch error:", error);
      message.error("Unable to load the consignment report.");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, currentPage, pageSize, requestBody]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues, selectedDate]);

  // Consignment Status + Loading Cycle Time charts (same as the AI report)
  const statusData = (report.statusChart || []).find((item) => item.Type === "Total") ?? {};
  const statusValues = [
    { label: "Unassigned", value: parseInt(statusData.Unassigned ?? 0, 10), color: "#FF5555" },
    { label: "Assigned", value: parseInt(statusData.Assigned ?? 0, 10), color: "rgb(108,167,255)" },
    { label: "In Progress", value: parseInt(statusData.Inprogress ?? 0, 10), color: "#FFBC41" },
    { label: "Completed", value: parseInt(statusData.Completed ?? 0, 10), color: "#69FF78" },
  ];
  const totalConsignments = statusValues.reduce((t, v) => t + v.value, 0);
  const totalLoadingCycle = (report.loadingTime || []).reduce((t, item) => t + (item.LoadingCycle ?? 0), 0);
  const loadingTimeData = (report.loadingTime || [])
    .filter((item) => (item.LoadingCycle ?? 0) > 0)
    .map((item, index) => ({
      name: item.CUSTNAME ?? "",
      value: item.LoadingCycle,
      time: hhmm(item.LoadingCycle),
      percent: totalLoadingCycle > 0 ? ((item.LoadingCycle / totalLoadingCycle) * 100).toFixed(2) : "0.00",
      color: LOADING_COLORS[index % LOADING_COLORS.length],
    }));

  const renderStatusLabel = ({ cx, cy, midAngle, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    const entry = statusValues[index];
    if (!entry) return null;
    const startX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const startY = cy + outerRadius * Math.sin(-midAngle * RADIAN);
    const breakX = cx + (outerRadius + 20) * Math.cos(-midAngle * RADIAN);
    const breakY = cy + (outerRadius + 20) * Math.sin(-midAngle * RADIAN);
    const isLeft = midAngle > 90 && midAngle < 270;
    const endX = breakX + (isLeft ? -60 : 60);
    const endY = breakY - index * 18;
    return (
      <g>
        <polyline points={`${startX},${startY} ${breakX},${breakY} ${endX},${endY}`} stroke="#BEBEBE" strokeWidth={1} fill="none" />
        <rect x={endX - (isLeft ? 14 : 0)} y={endY - 7} width={14} height={14} rx={2} ry={2} fill={entry.color} />
        <text x={endX + (isLeft ? -25 : 25)} y={endY + 5} textAnchor={isLeft ? "end" : "start"} fill="#5F6D8C" fontSize={14}>
          {`${entry.label}   ${entry.value} (${(percent * 100).toFixed(0)}%)`}
        </text>
      </g>
    );
  };

  const exportIcon = (id, title) => (
    <img
      src={ExportIcon}
      alt="Export"
      style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
      onClick={() => {
        if (!exportAllowed) return;
        handleDownloadChart(id, title);
      }}
    />
  );

  const valueOf = (row, field) =>
    field === "Started" || EVENT_COLUMNS.some((c) => c[1] === field) ? row.Events?.[field] : row[field];

  const sortRows = (rows) => {
    if (!tableSort.field) return rows;
    return [...rows].sort((a, b) => {
      const av = valueOf(a, tableSort.field);
      const bv = valueOf(b, tableSort.field);
      const r =
        typeof av === "number" || typeof bv === "number"
          ? (Number(av) || 0) - (Number(bv) || 0)
          : String(av ?? "").localeCompare(String(bv ?? ""));
      return tableSort.order === "ascend" ? r : -r;
    });
  };

  const col = (title, field, render) => ({
    title,
    dataIndex: field,
    key: field,
    sorter: true,
    sortOrder: tableSort.field === field ? tableSort.order : null,
    render: render || ((v) => v ?? "-"),
  });
  const columns = [
    col("Consign Code", "code", (t) => <a>{t}</a>),
    col("Pick Up Date and Time", "pickupdatetime", eventDate),
    col("Consignor", "custname", (v) => v || "-"),
    col("Consignee", "FNAME", (v) => v || "-"),
    col("Transporter", "SupName", (v) => v || "-"),
    col("Vehicle", "VehRegNo", (v) => v || "-"),
    col("Driver's Name", "drivername", (v) => v || "-"),
    col("Trip Type", "triptype", tripTypeLabel),
    col("Container No", "ContainerNo", (v) => v || "-"),
    ...EVENT_COLUMNS.map(([title, field]) => ({
      title,
      dataIndex: ["Events", field],
      key: field,
      sorter: true,
      sortOrder: tableSort.field === field ? tableSort.order : null,
      render: eventDate,
    })),
    col("Loading Cycle", "LoadingCycle", hhmm),
    col("Offloading Cycle", "OffloadingCycle", hhmm),
  ];

  const prepareCSV = async () => {
    try {
      setCSVLoader(true);
      const { data } = await CALL_API("POST", "nexus/consignmentreport", requestBody(1, 5000));
      const rows = sortRows(data?.data || []);
      if (!rows.length) {
        message.error("No data available to download.");
        return;
      }
      setCSVList(
        rows.map((r) => ({
          "Consign Code": r.code || "-",
          "Pick Up Date and Time": eventDate(r.pickupdatetime),
          Consignor: r.custname || "-",
          Consignee: r.FNAME || "-",
          Transporter: r.SupName || "-",
          Vehicle: r.VehRegNo || "-",
          "Driver's Name": r.drivername || "-",
          "Trip Type": tripTypeLabel(r.triptype),
          "Container No": r.ContainerNo || "-",
          ...Object.fromEntries(EVENT_COLUMNS.map(([title, field]) => [title, eventDate(r.Events?.[field])])),
          "Loading Cycle": hhmm(r.LoadingCycle),
          "Offloading Cycle": hhmm(r.OffloadingCycle),
        })),
      );
      setTimeout(() => csvLinkEl.current?.link?.click(), 300);
    } catch (error) {
      console.error("[NexusConsignmentReport] export error:", error);
      message.error("Unable to export the consignment report.");
    } finally {
      setCSVLoader(false);
    }
  };

  return (
    <Styledashboard>
      <div>
        <div className="total" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <div className="head" style={{ flex: 1 }}>Chart View</div>
          <div className="Custom" style={{ display: "flex", gap: "20px" }}>
            <div className="dropstyle">
              <Select value={duration} onChange={setDuration} className="plc" style={{ width: "100%" }}>
                {Duration.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="dropstyle1">
              <Space direction="vertical" size={12}>
                <RangePicker value={selectedDate} onChange={(d) => d && setSelectedDate(d)} />
              </Space>
            </div>
            <div>
              <Badge count={filterCount} className="badg" onClick={() => setIsModalOpen(true)}>
                <Avatar shape="circle" size="medium" src={filtericon} className="filterstyle" style={{ cursor: "pointer" }} />
              </Badge>
            </div>
          </div>
        </div>

        <Modal
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          width={900}
          footer={
            <ButtonFilled
              onClick={() => {
                form.resetFields();
                setFilterValues({});
                setFilterCount(0);
              }}
            >
              Clear All
            </ButtonFilled>
          }
        >
          <h6 className="Filter_for_clickablerow">Filter ({filterCount})</h6>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, all) => {
              setFilterValues(all);
              setFilterCount(Object.values(all).filter((v) => v && v.length !== 0).length);
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Form.Item label="Vehicle No" name="vehicleno">
                  <Select
                    className="w-100"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={vehicles.map((a) => ({ value: a.asset_id, label: a.asset_name }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Consignor" name="consignor">
                  <Select
                    className="w-100"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={consignors.map((f) => ({ value: String(f.geofence_id), label: f.name }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Transporter" name="transporter">
                  <Select
                    className="w-100"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={transporterList?.map((item) => ({ value: item.ID, label: item.Name }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Consignee" name="fence">
                  <Select
                    className="w-100"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={consignees.map((f) => ({ value: f.name, label: f.name }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Consign Code" name="consigncode">
                  <Input className="rounded-pill" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Trip Code" name="trip">
                  <Input className="rounded-pill" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Trip Type" name="triptype">
                  <Select className="w-100" allowClear>
                    <Option value={2}>Safari</Option>
                    <Option value={1}>Local</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Shipment Type" name="shipment">
                  <Select
                    className="w-100"
                    allowClear
                    options={shipmentType?.map((item) => ({ label: item.name, value: item.value }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        <Spin spinning={loading}>
          <div id="Export Chart">
            <div className="chart-box" style={{ height: isMobile ? "auto" : "400px" }}>
              <div className="chart-title-row">
                <p className="pb-0 mb-0" style={{ fontWeight: 500, fontSize: "16px", margin: 0 }}>
                  Consignment Status
                </p>
                {exportIcon("Export Chart", "Consignment Status")}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: isMobile ? 200 : "100%" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusValues}
                      startAngle={180}
                      endAngle={0}
                      innerRadius={isMobile ? 60 : 100}
                      outerRadius={isMobile ? 100 : 160}
                      dataKey="value"
                      cx="50%"
                      cy={isMobile ? "90%" : "65%"}
                      labelLine={false}
                      label={isMobile ? false : renderStatusLabel}
                    >
                      {statusValues.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {isMobile ? (
                <>
                  <div style={{ textAlign: "center", marginTop: 4 }}>
                    <div style={{ color: "#9ca3af", fontSize: 14 }}>Total Consignments</div>
                    <div className="consignment-value">{totalConsignments}</div>
                  </div>
                  <div className="legend-container" style={{ paddingTop: 12 }}>
                    {statusValues.map((v) => (
                      <div key={v.label} className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: v.color }} />
                        <span>
                          {v.label} <strong>{v.value}</strong>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ position: "absolute", top: "75%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none" }}>
                  <div style={{ color: "#9ca3af", fontSize: 14 }}>Total Consignments</div>
                  <div className="consignment-value">{totalConsignments}</div>
                </div>
              )}
            </div>
          </div>
        </Spin>
        <br />
        <Spin spinning={loading}>
          <div id="Export Chart2">
            <div className="chart-box">
              <div className="chart-title-row">
                <p className="pb-0 mb-0" style={{ fontWeight: 500, fontSize: "16px", margin: 0 }}>
                  Loading Cycle Time
                </p>
                {exportIcon("Export Chart2", "Loading Time")}
              </div>
              <LoadingCycleChart data={loadingTimeData} mobile={isMobile} />
            </div>
          </div>
        </Spin>
        <br />
        <br />
        <div className="header-container">
          <div className="head">{report.total} records found.</div>
          <div>
            <Spin spinning={CSVLoader}>
              <img
                src={ExportIcon}
                style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
                alt="Export"
                className="hover-image"
                onClick={() => {
                  if (!exportAllowed) return;
                  prepareCSV();
                }}
              />
            </Spin>
            <CSVLink data={CSVList} filename={"NexusConsignmentReport.csv"} ref={csvLinkEl} />
          </div>
        </div>
        <br />

        <StyledTable
          dataSource={sortRows(report.data).map((r, i) => ({ ...r, key: `${r.code}-${i}` }))}
          columns={columns}
          pagination={false}
          loading={loading}
          scroll={{ x: "max-content" }}
          onChange={(pagination, filters, sorter) =>
            setTableSort({
              field: Array.isArray(sorter.field) ? sorter.field[sorter.field.length - 1] : sorter.field,
              order: sorter.order,
            })
          }
        />
      </div>

      <ReusablePagination
        pageSize={pageSize}
        handlePageSizeChange={(v) => {
          setPageSize(v);
          setCurrentPage(1);
        }}
        currentPage={currentPage}
        total={report.total}
        handlePageChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
      />
    </Styledashboard>
  );
};

export default withNexusReportShell(NexusConsignmentReport);
